import React, { useState, useEffect, useRef } from 'react';
import { withRouter, useHistory } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';

import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';
import {
  fetchGoogleCalendarAvailability,
} from '../../util/api';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoney } from '../../util/currency';
import { getTitleAfterHyphen, truncateAtWord } from '../../util/string';
import { ensureListing } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import {
  timestampToDate,
  getStartOf,
  timeOfDayFromLocalToTimeZone,
  stringifyDateToISO8601,
  isSameDay,
} from '../../util/dates';
import { findRouteByRouteName, createResourceLocatorString } from '../../util/routes';
import { isSignupEmailTakenError } from '../../util/errors';
import {
  bookingResumeMatchesUser,
  clearBookingResumeDraft,
  getBookingResumeDraft,
  restoreBookingFlowSession,
  saveBookingResumeDraft,
} from '../../util/bookingResume';
import * as validators from '../../util/validators';
import getCountryCodes from '../../translations/countryCodes';
import { validateDiscountCodeAsync } from '../../util/discount';


// Global ducks
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { authenticationInProgress, login, signup } from '../../ducks/auth.duck';
import { fetchCurrentUser, setBookingFlowData } from '../../ducks/user.duck';
import { recoverPassword } from '../PasswordRecoveryPage/PasswordRecoveryPage.duck';

// Shared components
import {
  Page,
  LayoutSingleColumn,
  PrimaryButton,
  FieldSelect,
  Form,
  FieldTextInput,
  FieldPhoneNumberInput,
} from '../../components';

// Related components
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

// Page-specific imports
import {
  fetchTimeSlots,
  fetchTransactionLineItems,
  setInitialValues,
} from '../ListingPage/ListingPage.duck';
import { LoadingPage, ErrorPage } from '../ListingPage/ListingPage.shared';
import FieldCustomCountryPicker from '../../components/FieldCustomCountryPicker/FieldCustomCountryPicker';

import css from './ReservationContactPage.module.css';
import IconCard from '../../components/SavedCardDetails/IconCard/IconCard.js';
import BookingLoginModal from './BookingLoginModal';
import {
  MIXPANEL_EVENTS,
  listingMixpanelProperties,
  trackMixpanelEvent,
} from '../../util/mixpanel';

const { UUID, Money } = sdkTypes;

import {
  optimizeCloudinaryUrl,
  getCloudinarySrcSet,
  DESKTOP_HERO_SRCSET_WIDTHS,
  TABLET_SRCSET_WIDTHS,
  MOBILE_SRCSET_WIDTHS,
} from '../../util/cloudinaryHelpers';

const reservationContactStorageKey = listingId =>
  listingId ? `lamh.reservationContact.${listingId}` : 'lamh.reservationContact';

const getReservationContactSession = listingId => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const raw = window.sessionStorage.getItem(reservationContactStorageKey(listingId));
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    return {};
  }
};

const saveReservationContactSession = (listingId, data) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const previous = getReservationContactSession(listingId);
    window.sessionStorage.setItem(
      reservationContactStorageKey(listingId),
      JSON.stringify({ ...previous, ...data })
    );
  } catch (error) {
    // Session storage can be unavailable in restricted browser modes.
  }
};

// Safe date parsing helper to handle Dates, timestamps (strings/numbers), or ISO strings
const parseDate = val => {
  if (val instanceof Date) {
    return val;
  }
  if (typeof val === 'number') {
    return new Date(val);
  }
  if (typeof val === 'string') {
    if (/^\d+$/.test(val)) {
      return new Date(Number.parseInt(val, 10));
    }
    const d = new Date(val);
    if (!isNaN(d.getTime())) {
      return d;
    }
  }
  return null;
};

export const getBookedOptionsForDate = (bookedData, dateStr) => {
  if (!bookedData || !dateStr) return [];

  // Object format: { "2026-08-01": ["TEST ONE"] }
  if (typeof bookedData === 'object' && bookedData !== null && !Array.isArray(bookedData)) {
    const val = bookedData[dateStr];
    if (val) {
      return Array.isArray(val) ? val.map(v => (typeof v === 'string' ? v.trim() : v)) : [val];
    }
    return [];
  }

  // Array of objects format: [{ date: "2026-08-01", optionName: "TEST ONE" }]
  if (Array.isArray(bookedData)) {
    return bookedData
      .map(item => {
        if (typeof item === 'object' && item !== null && item.date === dateStr) {
          return item.optionName || item.name;
        }
        return null;
      })
      .filter(Boolean);
  }

  return [];
};

/**
 * ReservationDetailsPage - Shows listing details and guest selection
 * This page is part of the booking flow and displays information similar to OrderPanel
 * but as a standalone page without using a modal.
 *
 * @component
 */
const ReservationDetailsPageComponent = props => {
  const {
    currentUser,
    getListing,
    getOwnListing,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    onManageDisableScrolling,
    onFetchTimeSlots,
    onFetchTransactionLineItems,
    monthlyTimeSlots,
    timeSlotsForDate,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    callSetInitialValues,
    onInitializeCardPaymentData,
    onUpdateProfile,
    onSignup,
    onLogin,
    onRecoverPassword,
    onFetchCurrentUser,
    loginInProgress,
    loginError,
    recoveryInProgress,
    recoveryError,
    onSetBookingFlowData,
  } = props;

  const listingId = new UUID(rawParams.id);
  const currentListing = ensureListing(getListing(listingId));
  const storedContactSession = getReservationContactSession(rawParams.id);
  const contactState = { ...storedContactSession, ...(location.state || {}) };

  // Determine if this is a conversation flow or reservation flow
  const publicData = currentListing?.attributes?.publicData || {};
  const { bookingType } = contactState;
  const storedBookingType = bookingType || publicData?.bookingType || 'reservation';
  const isConversation = storedBookingType === 'conversation';

  const [selectedGuests, setSelectedGuests] = useState(isConversation ? '1' : '');
  const [seatsOptions, setSeatsOptions] = useState([1, 2, 3, 4, 5]);
  const [mounted, setMounted] = useState(false);
  const [accountableData, setAccountableDataState] = useState(null);
  const accountableDataRef = useRef(null);
  const setAccountableData = data => {
    setAccountableDataState(data);
    accountableDataRef.current = data;
  };
  const [bookingData, setBookingDataState] = useState(
    () => contactState.bookingData || null
  );

  const setBookingData = data => {
    setBookingDataState(data);
    saveReservationContactSession(rawParams.id, { bookingData: data });
  };

  const [guestData, setGuestDataState] = useState(
    () => contactState.guestDetails || null
  );

  const setGuestData = data => {
    setGuestDataState(data);
    saveReservationContactSession(rawParams.id, { guestDetails: data });
  };

  const [showReservationSummary, setShowReservationSummaryState] = useState(false);

  const [showDetailsForm, setShowDetailsFormState] = useState(true);

  const setShowDetailsForm = val => {
    setShowDetailsFormState(val);
  };

  const setShowReservationSummary = val => {
    setShowReservationSummaryState(val);
  };
  const [submittingDetails, setSubmittingDetails] = useState(false);
  const [signupError, setSignupError] = useState(null);
  const [bookingCapacity, setBookingCapacity] = useState(null);
  const [checkingBookingCapacity, setCheckingBookingCapacity] = useState(false);
  const [bookingCapacityError, setBookingCapacityError] = useState(null);
  const [selectedBookingDate, setSelectedBookingDate] = useState(null);
  const [sessionBookedOptions, setSessionBookedOptions] = useState({});

  const [activeHoverCard, setActiveHoverCard] = useState(false);
  const [pricingHoverDismissed, setPricingHoverDismissed] = useState(false);
  const hoverTimeoutRef = useRef(null);

  const handleMouseEnterCard = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setPricingHoverDismissed(false);
    setActiveHoverCard(true);
  };

  const handleMouseLeaveCard = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setActiveHoverCard(false);
    }, 150);
  };

  const handleClosePricingHoverCard = event => {
    event.preventDefault();
    event.stopPropagation();
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setActiveHoverCard(false);
    setPricingHoverDismissed(true);
  };

  const getCurrentDateStr = bookingStartTime => {
    if (bookingStartTime) {
      if (typeof bookingStartTime === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(bookingStartTime.slice(0, 10))) {
        return bookingStartTime.slice(0, 10);
      }
      const parsedTime = Number.isInteger(bookingStartTime)
        ? bookingStartTime
        : typeof bookingStartTime === 'string' && /^\d+$/.test(bookingStartTime)
          ? Number.parseInt(bookingStartTime, 10)
          : null;
      if (parsedTime) {
        const dateObj = new Date(parsedTime);
        return timeZone
          ? moment(dateObj).tz(timeZone).format('YYYY-MM-DD')
          : moment(dateObj).format('YYYY-MM-DD');
      }
      const d = new Date(bookingStartTime);
      if (!Number.isNaN(d.getTime())) {
        return timeZone
          ? moment(d).tz(timeZone).format('YYYY-MM-DD')
          : moment(d).format('YYYY-MM-DD');
      }
    }
    if (selectedBookingDate) return selectedBookingDate;
    if (selectedBookingDateRef.current) return selectedBookingDateRef.current;
    return null;
  };

  const getDailyCapacityForDate = (dateOrDay, listingAttributes = {}) => {
    const publicData = listingAttributes?.publicData || {};
    const availabilityPlan = listingAttributes?.availabilityPlan || {};
    const { seats, personPricing = [], priceVariants = [] } = publicData;
    const defaultSeats = (seats && Number.isInteger(seats) && seats > 0) ? seats : 1;

    let planSeats = null;
    let hasPlanForDay = false;
    if (dateOrDay && availabilityPlan.entries && Array.isArray(availabilityPlan.entries)) {
      const dayOfWeekNames = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
      const dateObj = new Date(dateOrDay);
      if (!Number.isNaN(dateObj.getTime())) {
        const dayOfWeekIndex = timeZone ? moment(dateObj).tz(timeZone).day() : dateObj.getDay();
        const dayOfWeekName = dayOfWeekNames[dayOfWeekIndex];
        const planEntry = availabilityPlan.entries.find(e => e.dayOfWeek === dayOfWeekName);
        hasPlanForDay = true;
        if (planEntry) {
          planSeats = (Number.isInteger(planEntry.seats) && planEntry.seats > 0)
            ? planEntry.seats
            : defaultSeats;
        } else {
          planSeats = 0;
        }
      }
    }

    if (hasPlanForDay && planSeats !== null) {
      return planSeats;
    }
    if (seats && Number.isInteger(seats) && seats > 0) {
      return seats;
    }
    if (Array.isArray(personPricing) && personPricing.length > 0) {
      return Math.max(...personPricing.map(p => p.numberOfPeople || 1));
    }
    if (Array.isArray(priceVariants) && priceVariants.length > 0) {
      const guestNums = priceVariants
        .filter(v => /guest|person|people|pax|seat/i.test(v.name || ''))
        .map(v => {
          const m = v.name?.match(/\d+/);
          return m ? parseInt(m[0], 10) : null;
        })
        .filter(Boolean);
      if (guestNums.length > 0) {
        return Math.max(...guestNums);
      }
    }

    return 1000;
  };

  const getActiveBookedOptions = bookingStartTime => {
    const dateStr = getCurrentDateStr(bookingStartTime);
    if (!dateStr) return [];

    const fromMongoMap = allBookedOptionsMap[dateStr] || [];
    const fromPublic = getBookedOptionsForDate(
      publicData.bookedOptions,
      dateStr
    );
    const fromSession = (sessionBookedOptions && dateStr && sessionBookedOptions[dateStr]) || [];
    const fromCapacity =
      bookingCapacity && bookingCapacity.date === dateStr && Array.isArray(bookingCapacity.bookedOptions)
        ? bookingCapacity.bookedOptions
        : [];

    const booked = fromMongoMap.length > 0 ? fromMongoMap : [...fromPublic, ...fromSession, ...fromCapacity];
    return booked;
  };

  const getActiveBookedSeats = bookingStartTime => {
    const dateStr = getCurrentDateStr(bookingStartTime);
    if (!dateStr) return 0;
    const bookedOptions = getActiveBookedOptions(bookingStartTime);
    let total = 0;
    bookedOptions.forEach(opt => {
      const num = typeof opt === 'number' ? opt : parseInt(String(opt).match(/\d+/)?.[0] || '1', 10);
      total += Number.isNaN(num) ? 1 : num;
    });
    return total;
  };
  const [loginModalState, setLoginModalState] = useState({
    isOpen: false,
    email: '',
    guestDetails: null,
    recoveryEmailSent: false,
  });
  const [pendingLoginContinuation, setPendingLoginContinuation] = useState(null);
  const restoredDraftRef = useRef(null);

  // Helper to generate a random secure password for guest registration
  const generateRandomPassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()';
    let password = '';
    for (let i = 0; i < 16; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const intl = useIntl();
  const history = useHistory();
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();

  const userPhoneNumber = currentUser?.attributes?.profile?.protectedData?.phoneNumber || '';
  let initialCountryCode = '+1';
  let initialPhoneNumber = '';
  const countryCodesList = getCountryCodes(intl.locale);
  let initialCountry = '';
  if (userPhoneNumber) {
    if (userPhoneNumber.startsWith('+')) {
      const match = userPhoneNumber.match(/^(\+\d{1,4})(.*)$/);
      if (match) {
        initialCountryCode = match[1];
        initialPhoneNumber = match[2].trim();
      } else {
        initialPhoneNumber = userPhoneNumber;
      }
    } else {
      initialPhoneNumber = userPhoneNumber;
    }
  }

  if (!initialCountry) {
    if (initialCountryCode === '+91') {
      initialCountry = 'India';
    } else if (initialCountryCode === '+1') {
      initialCountry = 'United States';
    } else if (initialCountryCode === '+44') {
      initialCountry = 'United Kingdom';
    } else if (initialCountryCode === '+61') {
      initialCountry = 'Australia';
    } else if (initialCountryCode === '+86') {
      initialCountry = 'China';
    }
  }

  const initialGuestValues = {
    fullName: currentUser?.attributes?.profile
      ? `${currentUser.attributes.profile.firstName || ''} ${currentUser.attributes.profile
        .lastName || ''}`.trim() || currentUser.attributes.profile.displayName
      : '',
    email: currentUser?.attributes?.email || '',
    countryCode: initialCountryCode,
    phoneNumber: initialPhoneNumber,
    country: initialCountry,
  };

  const cardSectionRef = useRef(null);

  const scrollToFormCard = () => {
    if (typeof window !== 'undefined') {
      const el = document.getElementById('form') || cardSectionRef.current;
      if (el) {
        const y = el.getBoundingClientRect().top + window.pageYOffset - 90;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      }
    }
  };

  useEffect(() => {
    setMounted(true);
    if (typeof window !== 'undefined') {
      const timer = setTimeout(() => {
        scrollToFormCard();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, []);

  const listingSlug = rawParams.slug || createSlug(currentListing?.attributes?.title || '');
  const trackingListingTitle = currentListing?.attributes?.title;

  const [googleCalendarError, setGoogleCalendarError] = useState(null);
  const [checkingGoogleCalendar, setCheckingGoogleCalendar] = useState(false);
  const [isHeroSubtitleExpanded, setIsHeroSubtitleExpanded] = useState(false);
  const prevValuesRef = useRef({ startTime: null, endTime: null });
  const bookingCapacityRequestRef = useRef(0);
  const selectedBookingDateRef = useRef(null);
  const priceSectionRef = useRef(null);
  const reservationPageStartedAtRef = useRef(null);
  const priceViewedRef = useRef(false);

  useEffect(() => {
    const priceSection = priceSectionRef.current;
    if (
      !currentListing?.id?.uuid ||
      !trackingListingTitle ||
      !priceSection ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return undefined;
    }

    trackMixpanelEvent(MIXPANEL_EVENTS.RESERVATION_CONTACT_VIEWED, {
      ...listingMixpanelProperties(currentListing),
    });

    reservationPageStartedAtRef.current =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    priceViewedRef.current = false;

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting) && !priceViewedRef.current) {
          priceViewedRef.current = true;
          const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
          const listingPrice = currentListing.attributes?.price;
          trackMixpanelEvent(MIXPANEL_EVENTS.PRICE_VIEWED, {
            ...listingMixpanelProperties(currentListing),
            price_view_location: 'reservation_details',
            booking_type: storedBookingType,
            amount: listingPrice?.amount || null,
            currency: listingPrice?.currency || null,
            seconds_on_reservation_page: reservationPageStartedAtRef.current
              ? Math.round((now - reservationPageStartedAtRef.current) / 1000)
              : null,
          });
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(priceSection);
    return () => observer.disconnect();
  }, [currentListing?.id?.uuid, trackingListingTitle, storedBookingType]);

  const checkBookingCapacity = async selectedCalendarDate => {
    const requestId = bookingCapacityRequestRef.current + 1;
    bookingCapacityRequestRef.current = requestId;

    if (!selectedCalendarDate) {
      setBookingCapacity(null);
      setBookingCapacityError(null);
      setCheckingBookingCapacity(false);
      setGoogleCalendarError(null);
      return false;
    }

    setCheckingBookingCapacity(false);
    setBookingCapacityError(null);
    setGoogleCalendarError(null);

    return true;
  };

  const handleBookingDateChange = date => {
    const selectedCalendarDate = date ? moment(date).format('YYYY-MM-DD') : null;

    if (selectedCalendarDate === selectedBookingDateRef.current) {
      return;
    }

    selectedBookingDateRef.current = selectedCalendarDate;
    setSelectedBookingDate(selectedCalendarDate);
    setBookingCapacity(null);
    setBookingCapacityError(null);
    setGoogleCalendarError(null);
    setCheckingGoogleCalendar(false);

    if (!selectedCalendarDate) {
      bookingCapacityRequestRef.current += 1;
      setBookingCapacity(null);
      setBookingCapacityError(null);
      setCheckingBookingCapacity(false);
      return;
    }

    checkBookingCapacity(selectedCalendarDate);
  };

  useEffect(() => {
    if (selectedBookingDate && !bookingCapacity) {
      checkBookingCapacity(selectedBookingDate);
    }
  }, [selectedBookingDate, bookingCapacity]);

  const handleFormValuesChange = async formState => {
    const { bookingStartTime, bookingEndTime } = formState.values;
    if (
      bookingStartTime === prevValuesRef.current.startTime &&
      bookingEndTime === prevValuesRef.current.endTime
    ) {
      return;
    }
    prevValuesRef.current = { startTime: bookingStartTime, endTime: bookingEndTime };

    const listingPubData = currentListing?.attributes?.publicData || {};
    const idsArray = listingPubData.serviceProviderIds || listingPubData.serviceproviderIds || [];
    let providerIds = [];
    if (Array.isArray(idsArray) && idsArray.length > 0) {
      providerIds = idsArray.map(p => p.id).filter(Boolean);
    } else {
      const singleId = listingPubData.serviceProviderId || listingPubData.serviceproviderId;
      if (singleId) {
        providerIds = [singleId];
      }
    }
    if (providerIds.length === 0 || !bookingStartTime) {
      setGoogleCalendarError(null);
      return;
    }

    const startDate = timestampToDate(Number.parseInt(bookingStartTime, 10));
    const endDate = bookingEndTime ? timestampToDate(Number.parseInt(bookingEndTime, 10)) : null;

    setCheckingGoogleCalendar(true);
    setGoogleCalendarError(null);

    try {
      let datesToCheck = [];
      if (startDate && endDate) {
        let current = new Date(startDate.getTime());
        while (current < endDate) {
          datesToCheck.push(stringifyDateToISO8601(current, timeZone));
          current = new Date(current.getTime() + 24 * 60 * 60 * 1000);
        }
      } else {
        datesToCheck.push(stringifyDateToISO8601(startDate, timeZone));
      }

      const listingIdStr = currentListing?.id?.uuid || currentListing?.id?.toString();
      for (const dateStr of datesToCheck) {
        const response = await fetchGoogleCalendarAvailability({
          date: dateStr,
          timezone: timeZone,
          providerIds: providerIds.join(','),
          listingId: listingIdStr,
        });

        if (response.hasTokens === false) {
          setGoogleCalendarError(null);
          setCheckingGoogleCalendar(false);
          return;
        }

        if (response.available === false || (response.busy && response.busy.length > 0 && (!response.slots || response.slots.length === 0))) {
          setGoogleCalendarError(
            response.error ||
            intl.formatMessage({
              id: 'BookingTimeForm.providerNotAvailableOnDate',
              defaultMessage:
                'Selected time is not available because one or more service providers are unavailable.',
            })
          );
          setCheckingGoogleCalendar(false);
          return;
        }
      }
    } catch (err) {
      console.error('Error checking Google Calendar availability:', err);
    } finally {
      setCheckingGoogleCalendar(false);
    }
  };

  const availabilityPlan = currentListing?.attributes?.availabilityPlan || {};
  const timeZone = availabilityPlan.timezone || 'Etc/UTC';
  const { unitType, priceVariants = [] } = publicData;

  useEffect(() => {
    const stateToStore = location.state
      ? {
        bookingType: location.state.bookingType,
        userType: location.state.userType,
        userId: location.state.userId,
        bookingData: location.state.bookingData,
        guestDetails: location.state.guestDetails,
      }
      : null;
    const cleanedState = stateToStore
      ? Object.entries(stateToStore).reduce((stored, [key, value]) => {
        return value == null ? stored : { ...stored, [key]: value };
      }, {})
      : {};

    if (Object.keys(cleanedState).length > 0) {
      saveReservationContactSession(rawParams.id, cleanedState);
    }
  }, [rawParams.id, location.state]);

  useEffect(() => {
    if (
      !currentUser?.id?.uuid ||
      !currentListing?.id?.uuid ||
      pendingLoginContinuation ||
      restoredDraftRef.current === 'signup_in_progress'
    ) {
      return;
    }

    const draft = getBookingResumeDraft();
    if (!draft || draft.listingId !== currentListing?.id?.uuid) {
      return;
    }
    if (!bookingResumeMatchesUser(draft, currentUser)) {
      clearBookingResumeDraft();
      return;
    }

    const draftKey = `${draft.createdAt}:${draft.listingId}`;
    if (restoredDraftRef.current === draftKey) {
      return;
    }
    restoredDraftRef.current = draftKey;
    restoreBookingFlowSession(draft);

    const restoredBookingData = {
      bookingStartTime: draft.bookingData?.bookingStartTime || null,
      bookingEndTime: draft.bookingData?.bookingEndTime || null,
      numberOfGuests: String(draft.bookingData?.numberOfGuests || ''),
      // Preserve applied promotional discount across draft restoration
      discountCode: draft.bookingData?.discountCode || null,
      discountPercentage: draft.bookingData?.discountPercentage || null,
    };
    const parsedStartTime = restoredBookingData.bookingStartTime
      ? parseDate(restoredBookingData.bookingStartTime)
      : null;
    const restoredDate = parsedStartTime ? stringifyDateToISO8601(parsedStartTime, timeZone) : null;
    const restoredLocalDate = restoredDate ? moment(restoredDate, 'YYYY-MM-DD').toDate() : null;
    const restoredBookingStartDate = restoredLocalDate
      ? {
        date:
          unitType === 'hour' || unitType === 'fixed'
            ? timeOfDayFromLocalToTimeZone(restoredLocalDate, timeZone)
            : restoredLocalDate,
      }
      : null;

    setBookingData({
      ...restoredBookingData,
      bookingStartDate: restoredBookingStartDate,
    });
    setSelectedGuests(restoredBookingData.numberOfGuests);
    setSelectedBookingDate(restoredDate);
    selectedBookingDateRef.current = restoredDate;
    const finishRestoration = async () => {
      const dateAvailable = restoredDate ? await checkBookingCapacity(restoredDate) : true;
      setShowDetailsForm(true);
      setShowReservationSummary(false);
      clearBookingResumeDraft();
      if (typeof window !== 'undefined') {
        scrollToFormCard(80);
      }
    };

    finishRestoration();
  }, [
    currentListing?.id?.uuid,
    currentUser?.id?.uuid,
    pendingLoginContinuation,
    timeZone,
    unitType,
  ]);

  useEffect(() => {
    const sessionState = getReservationContactSession(rawParams.id);
    const storedState = { ...sessionState, ...(location.state || {}) };
    const { userType } = storedState;
    const storedUserType =
      userType ||
      (currentUser ? 'Customer' : null) ||
      (typeof window !== 'undefined' && window.sessionStorage.getItem('bookingFlowUserType'));

    if (!isConversation && !storedUserType) {
      history.replace(
        createResourceLocatorString(
          'BookingFlowPage',
          routeConfiguration,
          { id: listingId.uuid, slug: listingSlug },
          {}
        )
      );
    }
  }, [isConversation, location.state, history, routeConfiguration, listingId, listingSlug, currentUser]);

  useEffect(() => {
    if (listingId && timeZone && currentListing?.id) {
      const TODAY = new Date();
      const startOfToday = getStartOf(TODAY, 'day', timeZone);

      const isFixed = unitType === 'fixed';

      const nextBoundary = startOfToday;
      const nextMonth = getStartOf(nextBoundary, 'month', timeZone, 1, 'months');
      const nextAfterNextMonth = getStartOf(nextMonth, 'month', timeZone, 1, 'months');

      const variants = priceVariants || [];
      const bookingLengthInMinutes = variants.reduce((min, priceVariant) => {
        return Math.min(min, priceVariant.bookingLengthInMinutes);
      }, Number.MAX_SAFE_INTEGER);

      const nextMonthEnd = isFixed
        ? getStartOf(nextMonth, 'minute', timeZone, bookingLengthInMinutes, 'minutes')
        : nextMonth;
      const followingMonthEnd = isFixed
        ? getStartOf(nextAfterNextMonth, 'minute', timeZone, bookingLengthInMinutes, 'minutes')
        : nextAfterNextMonth;

      const minDurationStartingInInterval = isFixed ? bookingLengthInMinutes : 60;

      const options = intervalAlign => {
        return ['fixed', 'hour'].includes(unitType)
          ? {
            extraQueryParams: {
              intervalDuration: 'P1D',
              intervalAlign,
              seats: 0,
              maxPerInterval: 1,
              minDurationStartingInInterval,
              perPage: 31,
              page: 1,
            },
          }
          : null;
      };

      onFetchTimeSlots(listingId, nextBoundary, nextMonthEnd, timeZone, options(startOfToday));
      onFetchTimeSlots(listingId, nextMonth, followingMonthEnd, timeZone, options(nextMonth));
    }
  }, [currentListing?.id, timeZone, unitType, priceVariants]);

  useEffect(() => {
    if (!currentUser?.id?.uuid || !pendingLoginContinuation) {
      return;
    }

    const {
      guestDetails: pendingGuestDetails,
      bookingData: pendingBookingData,
    } = pendingLoginContinuation;
    clearBookingResumeDraft();
    setSignupError(null);
    setLoginModalState({
      isOpen: false,
      email: '',
      guestDetails: null,
      recoveryEmailSent: false,
    });
    setPendingLoginContinuation(null);
    continueAfterAuthentication(pendingGuestDetails, pendingBookingData);
  }, [currentUser?.id?.uuid, pendingLoginContinuation]);

  const handleMonthChange = React.useCallback(
    monthDate => {
      if (!listingId || !timeZone) return;

      const TODAY = new Date();
      const startOfToday = getStartOf(TODAY, 'day', timeZone);

      const monthStart = getStartOf(monthDate, 'month', timeZone);
      const adjustedStart = monthStart.getTime() < startOfToday.getTime() ? startOfToday : monthStart;

      const nextMonth = getStartOf(monthStart, 'month', timeZone, 1, 'months');
      const nextAfterNextMonth = getStartOf(monthStart, 'month', timeZone, 2, 'months');

      const isFixed = unitType === 'fixed';
      const variants = priceVariants || [];
      const bookingLengthInMinutes = variants.reduce((min, priceVariant) => {
        return Math.min(min, priceVariant.bookingLengthInMinutes);
      }, Number.MAX_SAFE_INTEGER);

      const currentMonthEnd = isFixed
        ? getStartOf(nextMonth, 'minute', timeZone, bookingLengthInMinutes, 'minutes')
        : nextMonth;
      const followingMonthEnd = isFixed
        ? getStartOf(nextAfterNextMonth, 'minute', timeZone, bookingLengthInMinutes, 'minutes')
        : nextAfterNextMonth;

      const minDurationStartingInInterval = isFixed ? bookingLengthInMinutes : 60;

      const fetchOptions = intervalAlign => {
        return ['fixed', 'hour'].includes(unitType)
          ? {
            extraQueryParams: {
              intervalDuration: 'P1D',
              intervalAlign,
              seats: 0,
              maxPerInterval: 1,
              minDurationStartingInInterval,
              perPage: 31,
              page: 1,
            },
          }
          : null;
      };

      onFetchTimeSlots(listingId, adjustedStart, currentMonthEnd, timeZone, fetchOptions(adjustedStart));
      onFetchTimeSlots(listingId, nextMonth, followingMonthEnd, timeZone, fetchOptions(nextMonth));
    },
    [listingId, timeZone, unitType, priceVariants, onFetchTimeSlots]
  );

  const handleBack = () => {
    if (window.history.length > 1) {
      history.goBack();
    } else {
      history.push(
        createResourceLocatorString(
          'BookingFlowPage',
          routeConfiguration,
          { id: listingId.uuid, slug: listingSlug },
          {}
        )
      );
    }
  };

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing?.id) {
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const { price = null, title = '', description = '' } = currentListing?.attributes || {};

  const schemaTitle = intl.formatMessage(
    { id: 'ReservationDetailsPage.schemaTitle' },
    { title, marketplaceName: config.marketplaceName }
  );

  const listingSubtitle = description || publicData.subtitle || '';
  const heroLocationLine = publicData.location?.address || 'Khao Yai National Park';

  // Responsive hero images — same screen-size sources as ListingPageCarousel / ReservationDetailsPage
  const { goldenSection = {}, detailsSection = {} } = publicData;
  let customHeroImageUrl = null;
  let customHeroImageTabletUrl = null;
  let customHeroImageMobileUrl = null;

  if (publicData.screen_experience === 'golden_screen') {
    // Reservation flow always shows the details-section hero (like listing "show details")
    customHeroImageUrl = detailsSection?.heroImage?.url || goldenSection?.heroImage?.url;
    customHeroImageTabletUrl =
      detailsSection?.heroImageTab?.url || goldenSection?.heroImageTab?.url;
    customHeroImageMobileUrl =
      detailsSection?.heroImageMobile?.url || goldenSection?.heroImageMobile?.url;
  } else if (publicData.screen_experience === 'simple_screen') {
    customHeroImageUrl = detailsSection?.heroImage?.url;
    customHeroImageTabletUrl = detailsSection?.heroImageTab?.url;
    customHeroImageMobileUrl = detailsSection?.heroImageMobile?.url;
  }

  const heroImageUrl =
    customHeroImageUrl ||
    publicData.heroImageCloudinaryUrl ||
    require('../../assets/listingbackgroundcss.jpg');
  const heroImageTabletUrlOriginal = customHeroImageTabletUrl || heroImageUrl;
  const heroImageMobileUrlOriginal = customHeroImageMobileUrl || heroImageUrl;

  const isCloudinaryHero =
    heroImageUrl && typeof heroImageUrl === 'string' && heroImageUrl.includes('res.cloudinary.com');
  const optimizedHeroImageUrl = isCloudinaryHero
    ? optimizeCloudinaryUrl(heroImageUrl, 1920, '16:9', 'best')
    : heroImageUrl;
  const heroImageSrcSet = isCloudinaryHero
    ? getCloudinarySrcSet(heroImageUrl, '16:9', DESKTOP_HERO_SRCSET_WIDTHS, 'best')
    : null;

  const isCloudinaryTablet =
    heroImageTabletUrlOriginal &&
    typeof heroImageTabletUrlOriginal === 'string' &&
    heroImageTabletUrlOriginal.includes('res.cloudinary.com');
  const heroImageTabletUrl = isCloudinaryTablet
    ? optimizeCloudinaryUrl(heroImageTabletUrlOriginal, 1240, '3:4', 'best')
    : heroImageTabletUrlOriginal;
  const heroImageTabletSrcSet = isCloudinaryTablet
    ? getCloudinarySrcSet(heroImageTabletUrlOriginal, '3:4', TABLET_SRCSET_WIDTHS, 'best')
    : null;

  const isCloudinaryMobile =
    heroImageMobileUrlOriginal &&
    typeof heroImageMobileUrlOriginal === 'string' &&
    heroImageMobileUrlOriginal.includes('res.cloudinary.com');
  const heroImageMobileUrl = isCloudinaryMobile
    ? optimizeCloudinaryUrl(heroImageMobileUrlOriginal, 800, '9:16', 'best')
    : heroImageMobileUrlOriginal;
  const heroImageMobileSrcSet = isCloudinaryMobile
    ? getCloudinarySrcSet(heroImageMobileUrlOriginal, '9:16', MOBILE_SRCSET_WIDTHS, 'best')
    : null;

  const { days = [], startTimeInterval, experienceDuration } = publicData;
  const firstVariant = priceVariants[0] || {};
  const isFlat = firstVariant && 'numberOfPeople' in firstVariant;
  const personPricing =
    publicData.personPricing && publicData.personPricing.length > 0
      ? publicData.personPricing
      : (isFlat ? priceVariants : firstVariant.personPricing) || [];

  const hasOnePersonPricingInVariants = personPricing.some(p => p.numberOfPeople === 1);
  const formatPriceFromSubunits = priceInSubunits => {
    const priceInDollars = priceInSubunits / 100;
    return `US $${priceInDollars.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };
  const bookingLengthInMinutes =
    publicData.bookingLengthInMinutes || firstVariant.bookingLengthInMinutes || 0;

  const variants = priceVariants || [];
  const minBookingLength = variants.reduce((min, priceVariant) => {
    return Math.min(min, priceVariant.bookingLengthInMinutes);
  }, Number.MAX_SAFE_INTEGER);
  const minDurationStartingInInterval = unitType === 'fixed' ? minBookingLength : 60;

  // Extract tour details
  const times =
    publicData.days?.flatMap(day => day.timeline?.map(item => item.startTime || item.time)) || [];

  const getDaysFromMinutes = minutes => {
    const hours = minutes / 60;
    if (hours < 24) return 0.5;
    return hours / 24;
  };

  const tourDays = bookingLengthInMinutes > 0 ? getDaysFromMinutes(bookingLengthInMinutes) : 0;

  const formatAmPmOnly = timeStr => {
    if (!timeStr) return 'AM';
    const str = String(timeStr).trim().toUpperCase();

    if (str === 'AM' || str === 'PM') return str;

    if (str.includes('AFTERNOON') || str.includes('EVENING') || str.includes('NIGHT') || str.includes('PM')) {
      return 'PM';
    }
    if (str.includes('MORNING') || str.includes('AM')) {
      return 'AM';
    }

    const match = str.match(/^(\d{1,2})[:.]?(\d{2})?/);
    if (match) {
      const hours = parseInt(match[1], 10);
      if (!isNaN(hours)) {
        return hours >= 12 ? 'PM' : 'AM';
      }
    }
    return 'AM';
  };

  const expType = publicData.experience_type;
  const tourType = publicData?.tourDays || '';

  const startTime = times.length > 0 ? formatAmPmOnly(times[0]) : formatAmPmOnly(publicData?.experienceStartTime) || 'AM';

  const rawTransportMode = publicData?.transporationMode || publicData?.transportationMode || publicData?.transportation_mode;
  const modeOfTransport = rawTransportMode
    ? (Array.isArray(rawTransportMode) ? rawTransportMode.join(', ') : rawTransportMode)
    : 'Land+';

  const listingPriceAmount = price?.amount || 0;

  // Handle line items fetching
  const handleFetchLineItems = formValues => {
    const values = formValues?.values || formValues || {};
    const { bookingStartTime, bookingEndTime } = values;
    const numberOfGuests = values.numberOfGuests || formValues?.numberOfGuests || selectedGuests;

    const parsedNum = parseInt(numberOfGuests, 10);
    const guestsNum = Number.isNaN(parsedNum) ? 1 : parsedNum;
    // Find matching price variant based on guest count if personPricing is empty
    let priceVariantName = null;
    if (personPricing.length === 0 && priceVariants && priceVariants.length > 0) {
      const match = priceVariants.find(variant => {
        const variantGuests = parseInt(variant.name?.match(/\d+/)?.[0], 10);
        return variantGuests === guestsNum;
      });
      if (match) {
        priceVariantName = match.name;
      }
    }

    const startDate = bookingStartTime ? parseDate(bookingStartTime) : null;
    const isDay = unitType === 'day';
    const isHour = unitType === 'hour';
    const isFixed = unitType === 'fixed';

    // For 'day' bookings: auto-compute endDate as start of next day (exclusive end per Sharetribe API)
    let endDate = bookingEndTime ? parseDate(bookingEndTime) : null;
    if (isDay && startDate && !endDate) {
      endDate = getStartOf(startDate, 'day', timeZone, 1, 'days');
    }

    const isStartBeforeEnd = !startDate || !endDate || startDate < endDate;

    // For 'hour' and 'fixed', require an explicit end time; for 'day', end is auto-computed
    const hasRequiredDates =
      isHour || isFixed ? startDate && endDate && bookingEndTime : startDate && endDate;

    if (hasRequiredDates && isStartBeforeEnd && numberOfGuests && !fetchLineItemsInProgress) {
      const orderData = {
        bookingStart: startDate,
        bookingEnd: endDate,
        numberOfGuests: guestsNum,
        bookingType: storedBookingType,
        ...(priceVariantName ? { priceVariantName } : {}),
      };
      onFetchTransactionLineItems({
        orderData,
        listingId: currentListing?.id,
        isOwnListing: false,
      });
    }
  };

  const handleGuestChange = (value, form) => {
    setSelectedGuests(value);

    // Get current form values
    const formValues = form.getState().values;

    // For 'hour' and 'fixed': require both start and end time. For 'day': only start date needed.
    const isHour = unitType === 'hour';
    const isFixed = unitType === 'fixed';
    if (
      value &&
      formValues.bookingStartTime &&
      (!(isHour || isFixed) || formValues.bookingEndTime)
    ) {
      handleFetchLineItems({
        ...formValues,
        numberOfGuests: value,
      });
    }
  };

  const handleSubmit = async values => {
    if (!values.numberOfGuests) {
      alert(intl.formatMessage({ id: 'ReservationDetailsPage.selectGuestsError' }));
      return;
    }

    const selectedCalendarDate =
      selectedBookingDateRef.current ||
      (values.bookingStartTime
        ? stringifyDateToISO8601(parseDate(values.bookingStartTime), timeZone)
        : null);
    const hasBookingCapacity = await checkBookingCapacity(selectedCalendarDate);
    if (!hasBookingCapacity) {
      return;
    }

    // Store booking data and show details form
    setBookingData(values);
    setAccountableData(null);
    setShowDetailsForm(true);
    if (typeof window !== 'undefined') {
      document.getElementById('form')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getBookingFlowContext = () => {
    const sessionState = getReservationContactSession(rawParams.id);
    const storedState = { ...sessionState, ...(location.state || {}) };
    const {
      userType,
      userId,
      bookingType: locationBookingType,
    } = storedState;
    return {
      userType: userType || null,
      userId: userId || null,
      bookingType: locationBookingType || 'reservation',
    };
  };

  const saveCurrentBookingResume = (email, guestDetails) => {
    const flowContext = getBookingFlowContext();
    return saveBookingResumeDraft({
      email,
      listingId: currentListing?.id?.uuid,
      listingSlug,
      bookingType: flowContext.bookingType,
      userType: flowContext.userType,
      userId: flowContext.userId,
      bookingData: bookingData || getReservationContactSession(rawParams.id).bookingData,
      guestDetails: guestDetails || guestData,
      restoreStage: 'reservation-summary',
    });
  };

  const continueAfterAuthentication = (details, pendingBookingData) => {
    const { fullName, countryCode, phoneNumber } = details || {};
    if (fullName) {
      const nameParts = fullName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || ' ';

      onUpdateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim() || ' ',
        protectedData: {
          phoneNumber: `${countryCode || ''}${phoneNumber || ''}`.trim(),
        },
      }).catch(err => {
        console.error('Failed to update profile:', err);
      });
    }

    const dataToUse =
      pendingBookingData ||
      bookingData ||
      location.state?.bookingData ||
      getReservationContactSession(rawParams.id).bookingData;

    // Keep session storage synchronized with active booking data including discount
    saveReservationContactSession(rawParams.id, {
      bookingData: dataToUse,
      guestDetails: details || guestData,
    });

    if (onSetBookingFlowData) {
      onSetBookingFlowData({
        bookingData: dataToUse,
        guestDetails: details || guestData,
        userType: location.state?.userType || 'Customer',
      });
    }

    clearBookingResumeDraft();
    setSubmittingDetails(false);
    handleProceedToCheckout(dataToUse, details);
  };

  const handleBookingLogin = password => {
    const { email, guestDetails } = loginModalState;
    setPendingLoginContinuation({
      guestDetails,
      bookingData:
        bookingData ||
        location.state?.bookingData ||
        getReservationContactSession(rawParams.id).bookingData,
    });
    return onLogin(email, password).catch(error => {
      setPendingLoginContinuation(null);
      throw error;
    });
  };

  const handleForgotPassword = () => {
    const { email, guestDetails } = loginModalState;
    saveCurrentBookingResume(email, guestDetails);
    return onRecoverPassword(email).then(() => {
      setLoginModalState(previous => ({ ...previous, recoveryEmailSent: true }));
    });
  };

  const handleDetailsSubmit = async guestDetails => {
    const flowContext = getBookingFlowContext();
    const isConcierge = flowContext.userType === 'Concierge';

    // Store guest details
    setGuestData(guestDetails);

    let activeBookingData =
      bookingData ||
      location.state?.bookingData ||
      getReservationContactSession(rawParams.id).bookingData ||
      null;

    // Verify whether the applied discount code was already consumed by this email
    const verificationEmail = guestDetails?.email || currentUser?.attributes?.email;
    if (activeBookingData?.discountCode && verificationEmail) {
      try {
        const discountCheck = await validateDiscountCodeAsync(
          activeBookingData.discountCode,
          verificationEmail.trim().toLowerCase(),
          currentUser?.id?.uuid || null
        );

        if (!discountCheck.valid && discountCheck.error === 'ALREADY_USED') {
          alert(
            intl.formatMessage({
              id: 'ReservationContactPage.alreadyUsedDiscount',
              defaultMessage:
                'This discount code has already been used by this email address. The reservation will continue without the discount.',
            })
          );

          activeBookingData = {
            ...activeBookingData,
            discountCode: null,
            discountPercentage: null,
          };
          setBookingData(activeBookingData);
        }
      } catch (checkErr) {
        // Network fallback
      }
    }

    if (!currentUser) {
      setSubmittingDetails(true);
      setSignupError(null);

      const { fullName, email, countryCode, phoneNumber, country } = guestDetails;
      const nameParts = fullName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || ' ';
      const generatedPassword = generateRandomPassword();

      const signupParams = {
        email: email.trim(),
        password: generatedPassword,
        firstName: firstName.trim(),
        lastName: lastName.trim() || ' ',
        publicData: {
          userType: 'customer',
          isGuest: true,
        },
        protectedData: {
          phoneNumber: `${countryCode}${phoneNumber}`.trim(),
          country,
        },
      };

      saveReservationContactSession(rawParams.id, {
        bookingType: flowContext.bookingType,
        userType: flowContext.userType || 'Customer',
        userId: flowContext.userId,
        bookingData: activeBookingData,
        guestDetails,
        redirectToCheckout: false,
      });

      // Save draft to localStorage so full page reload restores bookingData & dates cleanly
      saveBookingResumeDraft({
        email: email.trim(),
        listingId: currentListing?.id?.uuid,
        listingSlug,
        bookingType: flowContext.bookingType,
        userType: flowContext.userType || 'Customer',
        userId: flowContext.userId,
        bookingData: activeBookingData,
        restoreStage: 'reservation-summary',
      });
      restoredDraftRef.current = 'signup_in_progress';

      onSignup(signupParams)
        .then(() => {

          // Store in Redux store
          if (onSetBookingFlowData) {
            onSetBookingFlowData({
              bookingData: activeBookingData,
              guestDetails,
              userType: location.state?.userType || 'Customer',
            });
          }

          setSubmittingDetails(false);
          setShowDetailsForm(false);
          continueAfterAuthentication(guestDetails, activeBookingData);
        })
        .catch(err => {
          console.error('[ReservationContactPage] Failed to create customer during guest checkout:', err);
          setSubmittingDetails(false);
          setShowReservationSummary(false);
          setShowDetailsForm(true);
          if (isSignupEmailTakenError(err)) {
            saveCurrentBookingResume(email, guestDetails);
            setLoginModalState({
              isOpen: true,
              email: email.trim().toLowerCase(),
              guestDetails,
              recoveryEmailSent: false,
            });
          } else {
            setSignupError(err);
          }
        });
    } else {
      continueAfterAuthentication(guestDetails, activeBookingData);
    }
  };

  const handleProceedToCheckout = (customBookingData, customGuestData) => {
    setShowDetailsForm(false);
    clearBookingResumeDraft();
    const sessionState = getReservationContactSession(rawParams.id);
    const storedState = { ...sessionState, ...(location.state || {}) };

    const isEvent =
      customBookingData && (customBookingData.preventDefault || customBookingData.target);
    const dataToUse =
      customBookingData && !isEvent ? customBookingData : bookingData || storedState.bookingData;
    const guestDataToUse = customGuestData || guestData || storedState.guestDetails;

    // Retrieve userType, userId and bookingType from state
    const { userType, userId, bookingType } = storedState;
    const storedUserType = userType || null;
    const storedUserId = userId || null;
    const storedBookingType = bookingType || 'reservation';

    const selectedPrice = getSelectedPrice(dataToUse?.numberOfGuests);
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    trackMixpanelEvent(MIXPANEL_EVENTS.CHECKOUT_STARTED, {
      ...listingMixpanelProperties(currentListing),
      booking_type: storedBookingType,
      seats: Number.parseInt(dataToUse?.numberOfGuests, 10) || null,
      amount: selectedPrice?.amount || null,
      currency: selectedPrice?.currency || null,
      seconds_on_reservation_page: reservationPageStartedAtRef.current
        ? Math.round((now - reservationPageStartedAtRef.current) / 1000)
        : null,
    });

    // Prepare booking dates
    let checkoutStartDate = dataToUse?.bookingStartTime
      ? parseDate(dataToUse.bookingStartTime)
      : null;

    if (!checkoutStartDate && (isConversation || storedBookingType === 'conversation')) {
      const tomorrow = moment().add(1, 'days').toDate();
      checkoutStartDate = getStartOf(tomorrow, 'day', timeZone);
    }

    // Auto-compute endDate if missing so CheckoutPage validation always passes
    let checkoutEndDate = dataToUse?.bookingEndTime ? parseDate(dataToUse.bookingEndTime) : null;
    if (checkoutStartDate && !checkoutEndDate) {
      if (unitType === 'day') {
        checkoutEndDate = getStartOf(checkoutStartDate, 'day', timeZone, 1, 'days');
      } else if (unitType === 'hour') {
        const lengthInMins = publicData?.bookingLengthInMinutes || 60;
        checkoutEndDate = new Date(checkoutStartDate.getTime() + lengthInMins * 60 * 1000);
      } else {
        checkoutEndDate = new Date(checkoutStartDate.getTime() + 24 * 60 * 60 * 1000);
      }
    }

    const bookingMaybe =
      checkoutStartDate && checkoutEndDate
        ? {
          bookingDates: {
            bookingStart: checkoutStartDate,
            bookingEnd: checkoutEndDate,
          },
        }
        : {};

    // Prepare quantity
    const parsedQuantity = Number.parseInt(dataToUse?.numberOfGuests, 10);
    const quantity = Number.isNaN(parsedQuantity) ? 1 : parsedQuantity;
    const quantityMaybe = Number.isInteger(quantity) ? { quantity } : {};

    // Find matching price variant based on guest count if personPricing is empty
    let priceVariantName = null;
    if (personPricing.length === 0 && priceVariants && priceVariants.length > 0) {
      const match = priceVariants.find(variant => {
        const variantGuests = parseInt(variant.name?.match(/\d+/)?.[0], 10);
        return variantGuests === quantity;
      });
      if (match) {
        priceVariantName = match.name;
      }
    }

    const isConcierge = storedUserType === 'Concierge';

    // Prepare initial values for checkout
    const initialValues = {
      listing: currentListing,
      orderData: {
        ...bookingMaybe,
        ...quantityMaybe,
        seats: quantity,
        userType: storedUserType,
        userId: storedUserId,
        bookingType: storedBookingType,
        transactionProcessAlias: publicData?.transactionProcessAlias || 'default-booking/release-1',
        tourType,
        startTime,
        modeOfTransport,
        ...(dataToUse?.discountCode ? { discountCode: dataToUse.discountCode, discountPercentage: dataToUse.discountPercentage } : {}),
        ...(priceVariantName ? { priceVariantName } : {}),
        accountableName: isConcierge ? (guestDataToUse?.fullName || null) : null,
        accountableEmail: isConcierge ? (guestDataToUse?.email || null) : null,
        accountablePhoneNumber: isConcierge
          ? (guestDataToUse?.phoneNumber
            ? `${guestDataToUse.countryCode || ''}${guestDataToUse.phoneNumber}`.trim()
            : null)
          : null,
        accountableCountry: isConcierge ? (guestDataToUse?.country || null) : null,
      },
      confirmPaymentError: null,
    };

    const saveToSessionStorage = true;

    // Set initial values for CheckoutPage
    const { setInitialValues: setCheckoutInitialValues } = findRouteByRouteName(
      'CheckoutPage',
      routeConfiguration
    );

    callSetInitialValues(setCheckoutInitialValues, initialValues, saveToSessionStorage);

    // Clear previous Stripe errors from store if there is any
    onInitializeCardPaymentData();

    if (storedBookingType === 'conversation') {
      const checkoutUrl = createResourceLocatorString(
        'CheckoutPage',
        routeConfiguration,
        { id: currentListing?.id?.uuid, slug: createSlug(currentListing?.attributes?.title || '') },
        {}
      );
      history.push(checkoutUrl, {
        orderData: initialValues.orderData,
        listing: currentListing,
      });
    } else {
      // Redirect to ReservationSummaryPage for reservation flow
      history.push({
        pathname: `/l/${listingSlug}/${currentListing?.id?.uuid}/reservation-summary`,
        state: {
          bookingData: dataToUse,
          guestDetails: guestDataToUse,
          userType: storedUserType,
          bookingType: storedBookingType,
        },
      });
    }
  };

  const getSelectedPrice = guestsCount => {
    if (!guestsCount) return price;
    const guestsNum = parseInt(guestsCount, 10);
    const option = personPricing?.find(p => p.numberOfPeople === guestsNum);
    if (option) {
      return new Money(option.priceInSubunits, price.currency);
    }
    if (priceVariants && priceVariants.length > 0) {
      const match = priceVariants.find(variant => {
        const variantGuests = parseInt(variant.name?.match(/\d+/)?.[0], 10);
        return variantGuests === guestsNum;
      });
      if (match) {
        return new Money(match.priceInSubunits, price.currency);
      }
    }
    return price;
  };

  const renderMainCard = () => {
    const flowContext = getBookingFlowContext();
      const isConcierge = flowContext.userType === 'Concierge';

      const fullNameRequired = validators.required(
        intl.formatMessage({ id: 'BookingFixedDurationForm.fullNameRequired' })
      );
      const fullNameAlphaNumeric = validators.isAlphaNumeric(
        intl.formatMessage({ id: 'BookingFixedDurationForm.fullNameAlphaNumeric' })
      );
      const fullNameValidators = validators.composeValidators(fullNameRequired, fullNameAlphaNumeric);
      const emailRequired = validators.required(
        intl.formatMessage({ id: 'BookingFixedDurationForm.emailRequired' })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({ id: 'BookingFixedDurationForm.emailInvalid' })
      );
      const phoneRequired = validators.required(
        intl.formatMessage({ id: 'BookingFixedDurationForm.phoneRequired' })
      );
      const countryRequired = validators.required(
        intl.formatMessage({ id: 'BookingFixedDurationForm.countryRequired' })
      );

      const formInitialValues = isConcierge
        ? {
          fullName: '',
          email: '',
          countryCode: initialCountryCode,
          phoneNumber: '',
          country: '',
        }
        : initialGuestValues;

      const onFormSubmit = values => {
        handleDetailsSubmit(values);
      };

      const handleBackClick = () => {
        if (window.history.length > 1) {
          history.goBack();
        } else {
          handleBack();
        }
      };

      return (
        <div className={isConversation ? css.conversationCard : css.detailsCard}>
          <FinalForm
            onSubmit={onFormSubmit}
            initialValues={formInitialValues}
            render={({ handleSubmit: handleGuestSubmit }) => (
              <Form onSubmit={handleGuestSubmit} className={css.guestForm}>
                <div className={css.cardContent}>
                  <button type="button" className={css.backBtn} onClick={handleBackClick}>
                    <FormattedMessage id="ReservationDetailsPage.backButton" />
                  </button>
                  <h3 className={css.yourDetailsHeading}>
                    {isConcierge ? (
                      <FormattedMessage id="BookingFixedDurationForm.guestDetails" />
                    ) : (
                      <FormattedMessage id="BookingFixedDurationForm.yourDetails" />
                    )}
                  </h3>

                  {signupError && (
                    <div
                      style={{
                        color: 'var(--colorFail)',
                        marginBottom: '16px',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                    >
                      {isSignupEmailTakenError(signupError) ? (
                        <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
                      ) : (
                        <FormattedMessage id="AuthenticationPage.signupFailed" />
                      )}
                    </div>
                  )}

                  <div className={css.guestSection}>
                    {!isConcierge ? (
                      <h4 className={css.guestLabel}>
                        <FormattedMessage id="BookingFixedDurationForm.guest01" />
                      </h4>
                    ) : null}

                    <FieldTextInput
                      id="fullName"
                      name="fullName"
                      type="text"
                      label={intl.formatMessage({ id: 'BookingFixedDurationForm.fullNameLabel' })}
                      labelClassName={css.fieldLabel}
                      inputRootClass={css.reservationInput}
                      placeholder={intl.formatMessage({
                        id: 'BookingFixedDurationForm.fullNamePlaceholder',
                      })}
                      className={css.field}
                      disabled={
                        isConcierge ? false : !!(currentUser && initialGuestValues.fullName)
                      }
                      validate={fullNameValidators}
                    />

                    <FieldTextInput
                      id="email"
                      name="email"
                      type="email"
                      label={intl.formatMessage({ id: 'BookingFixedDurationForm.emailLabel' })}
                      labelClassName={css.fieldLabel}
                      inputRootClass={css.reservationInput}
                      placeholder={intl.formatMessage({
                        id: 'BookingFixedDurationForm.emailPlaceholder',
                      })}
                      className={css.field}
                      disabled={
                        isConcierge ? false : !!(currentUser && initialGuestValues.email)
                      }
                      validate={validators.composeValidators(emailRequired, emailValid)}
                    />

                    <FieldPhoneNumberInput
                      id="phoneNumber"
                      name="phoneNumber"
                      countryCodeName="countryCode"
                      phoneNumberName="phoneNumber"
                      label={intl.formatMessage({ id: 'BookingFixedDurationForm.phoneLabel' })}
                      labelClassName={css.fieldLabel}
                      placeholder={intl.formatMessage({
                        id: 'BookingFixedDurationForm.phonePlaceholder',
                      })}
                      rootClassName={css.field}
                      useScrollableCountrySelect
                      countrySelectMaxHeight={240}
                      disabled={
                        isConcierge
                          ? false
                          : !!(currentUser && initialGuestValues.phoneNumber)
                      }
                      validate={phoneRequired}
                    />

                    <FieldCustomCountryPicker
                      id="country"
                      name="country"
                      label={intl.formatMessage({ id: 'BookingFixedDurationForm.countryLabel' })}
                      labelClassName={css.fieldLabel}
                      placeholder={intl.formatMessage({ id: 'BookingFixedDurationForm.countryPlaceholder' })}
                      className={css.field}
                      disabled={
                        isConcierge ? false : !!(currentUser && initialGuestValues.country)
                      }
                      validate={countryRequired}
                      options={countryCodesList.map(c => ({ value: c.name, label: c.name }))}
                      maxHeight={240}
                    />
                  </div>
                </div>

                <PrimaryButton
                  type="submit"
                  className={css.submitBtn}
                  disabled={submittingDetails}
                  inProgress={submittingDetails}
                >
                  {isConversation ? (
                    <>
                      <FormattedMessage id="ReservationDetailsPage.proceedCommitment" />
                      <IconCard brand="rightArrow" />
                    </>
                  ) : (
                    <>
                      <FormattedMessage id="BookingFixedDurationForm.continue" />
                      <IconCard brand="rightArrow" />
                    </>
                  )}
                </PrimaryButton>
              </Form>
            )}
          />
        </div>
      );
  };

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn
        topbar={topbar}
        footer={<FooterContainer />}
        mainColumnClassName={css.fullWidthMain}
      >
        <div className={isConversation ? css.conversationRoot : css.root}>
          {isConversation ? (
            <>
              <div className={css.conversationHeroWrapper}>
                {/* <img
                  src={require('../../assets/listingbackgroundcss.jpg')}
                  alt="Reservation Details"
                  className={css.conversationHeroImage}
                /> */}
                <div className={css.conversationHeroOverlay} />
                <div className={css.conversationHeroContent}>
                  <div className={css.titleLogoline}>{title ? title.toUpperCase() : ''}</div>
                  <h1 className={css.conversationHeroTitle}>
                    <FormattedMessage id="ReservationDetailsPage.beginYourJourney" />
                  </h1>
                  <p className={css.conversationHeroSubtitle}>
                    <FormattedMessage id="ReservationDetailsPage.journeyDisclaimer" />
                  </p>
                </div>
              </div>

              <div ref={cardSectionRef} className={css.conversationCardSection}>
                {renderMainCard()}
                {showDetailsForm ? (
                  <ul className={css.paymentDisclaimer}>
                    <li>
                      <FormattedMessage id="ReservationDetailsPage.disclaimerPay" />
                    </li>
                    <li>
                      <FormattedMessage id="ReservationDetailsPage.disclaimerCustomizable" />
                    </li>
                    <li>
                      <FormattedMessage id="ReservationDetailsPage.disclaimerRefine" />
                    </li>
                    <li>
                      <FormattedMessage id="ReservationDetailsPage.disclaimerConfirmation" />
                    </li>
                  </ul>
                ) : null}
              </div>
            </>
          ) : (
            <>
              <div className={css.staticHeroWrapper}>
                <picture>
                  {/* Mobile: 9:16 portrait crop */}
                  <source
                    media="(max-width: 600px)"
                    srcSet={heroImageMobileSrcSet || heroImageMobileUrl}
                    sizes="100vw"
                    width="600"
                    height="1067"
                  />
                  {/* Tablet: 3:4 portrait crop */}
                  <source
                    media="(max-width: 1240px)"
                    srcSet={heroImageTabletSrcSet || heroImageTabletUrl}
                    sizes="100vw"
                    width="1240"
                    height="1365"
                  />
                  {/* Desktop fallback: 16:9 landscape */}
                  <img
                    src={optimizedHeroImageUrl}
                    srcSet={heroImageSrcSet}
                    sizes="100vw"
                    alt={title}
                    className={css.heroImage}
                    fetchPriority="high"
                    decoding="async"
                    width="1920"
                    height="1080"
                  />
                </picture>
                <div className={css.heroOverlay} />
                <div className={css.heroContent}>
                  {/* <div className={css.titleLogoline}>{heroLocationLine}</div> */}
                  <h1 className={css.heroTitle}>{title}</h1>
                  {listingSubtitle ? (
                    <>
                      <p className={css.heroSubtitle}>
                        {listingSubtitle.length > 70 && !isHeroSubtitleExpanded ? (
                          <>
                            <span className={css.heroSubtitleFull}>{listingSubtitle}</span>
                            <span className={css.heroSubtitleTruncated}>
                              {truncateAtWord(listingSubtitle, 70)}
                            </span>
                          </>
                        ) : (
                          listingSubtitle
                        )}
                      </p>
                      {listingSubtitle.length > 70 ? (
                        <button
                          type="button"
                          className={css.heroReadMore}
                          onClick={() => setIsHeroSubtitleExpanded(prev => !prev)}
                        >
                          <FormattedMessage
                            id={
                              isHeroSubtitleExpanded
                                ? 'ReservationDetailsPage.readLess'
                                : 'ReservationDetailsPage.readMore'
                            }
                          />
                          <IconCard brand="rightArrow" />
                        </button>
                      ) : null}
                    </>
                  ) : null}
                </div>
              </div>

              <div ref={cardSectionRef} className={css.cardSection}>{renderMainCard()}</div>
            </>
          )}
        </div>
      </LayoutSingleColumn>
      <BookingLoginModal
        isOpen={loginModalState.isOpen}
        email={loginModalState.email}
        onClose={() =>
          setLoginModalState(previous => ({
            ...previous,
            isOpen: false,
            recoveryEmailSent: false,
          }))
        }
        onSubmit={handleBookingLogin}
        onForgotPassword={handleForgotPassword}
        onManageDisableScrolling={onManageDisableScrolling}
        loginInProgress={loginInProgress}
        loginError={loginError}
        recoveryInProgress={recoveryInProgress}
        recoveryError={recoveryError}
        recoveryEmailSent={loginModalState.recoveryEmailSent}
      />
    </Page>
  );
};

const mapStateToProps = state => {
  const {
    showListingError,
    monthlyTimeSlots,
    timeSlotsForDate,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
  } = state.ListingPage;
  const { currentUser } = state.user;
  const { loginError } = state.auth;
  const { recoveryInProgress, recoveryError } = state.PasswordRecoveryPage;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    showListingError,
    monthlyTimeSlots,
    timeSlotsForDate,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    loginInProgress: authenticationInProgress(state, 'loginInProgress'),
    loginError,
    recoveryInProgress,
    recoveryError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  callSetInitialValues: (setInitialValuesFunc, values, saveToSessionStorage) =>
    dispatch(setInitialValuesFunc(values, saveToSessionStorage)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onUpdateProfile: data => dispatch(updateProfile(data)),
  onSignup: params => dispatch(signup(params)),
  onLogin: (email, password) => dispatch(login(email, password)),
  onRecoverPassword: email => dispatch(recoverPassword({ email })).unwrap(),
  onFetchCurrentUser: options => dispatch(fetchCurrentUser(options)),
  onSetBookingFlowData: data => dispatch(setBookingFlowData(data)),
});

const ReservationContactPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ReservationDetailsPageComponent);

export default ReservationContactPage;
